// program_share.js — share a program as a link (and QR). Pure + async, no React.
//
//   encodeProgramLink(program, { exercises, base })  → Promise<{ url, token, bytes }>
//   decodeProgramToken(token)                          → Promise<shared> (throws on a bad link)
//   tokenFromText(text)                                → token | null  (a pasted link, '#p=…' or a bare token)
//   packProgram(program, exercises) / unpackProgram(packed)   (sync, the wire format)
//   materializeShared(shared, { exercises, catalog })  → { program, newExercises, report }
//
// What travels: name, split, length, the cycle, every day (name, weekday)
// and every exercise slot (sets, reps, rest, superset, scheme + its config).
// What never travels: training maxes, target weights, scheme progress, history,
// ids of the sender's program/days, videos.
//
// Token: one flag char + base64url. 'z' = deflate-raw (CompressionStream when
// the browser has it; inflate falls back to the small decoder below), 'j' =
// plain JSON (encoders without CompressionStream). Packed JSON is array-based
// to stay short enough for a QR code.

export const SHARE_VERSION = 1;
const SCHEME_CODES = { linear: 'l', percent: 'p', rpe_topset: 'r', gzcl: 'g', rp_sets: 'm' };
const CODE_SCHEMES = Object.fromEntries(Object.entries(SCHEME_CODES).map(([k, v]) => [v, k]));

// ——— Pack / unpack ———————————————————————————————————————————————————————

const trimTail = (arr) => { while (arr.length && (arr[arr.length - 1] == null || arr[arr.length - 1] === '')) arr.pop(); return arr; };

export function packProgram(program, exercises = []) {
  const exIndex = new Map();
  const table = [];
  const ref = (id, pe) => {
    const key = id || `name:${pe?.name}`;
    if (exIndex.has(key)) return exIndex.get(key);
    const ex = exercises.find(e => e.id === id) || null;
    const name = ex?.name || pe?.name || 'Exercise';
    let row;
    if (id && /^fx_/.test(id)) row = [id];
    else if (id && /^x_/.test(id)) row = [id, name];
    else row = trimTail([id || '', name, ex?.group || pe?.group || 'Other', ex?.equipment || '', (ex?.unit || pe?.unit) === 'sec' ? 's' : '']);
    table.push(row);
    exIndex.set(key, table.length - 1);
    return table.length - 1;
  };
  const days = (program.days || []).map(d => {
    const ss = new Map();
    const list = (d.exercises || []).map(pe => {
      const xi = ref(pe.exerciseId, pe);
      let cfg = null;
      const code = SCHEME_CODES[pe.scheme] || null;
      if (pe.schemeCfg && Object.keys(pe.schemeCfg).length) {
        cfg = { ...pe.schemeCfg };
        if (cfg.tmKey) { if (cfg.tmKey !== pe.exerciseId) cfg.tk = ref(cfg.tmKey, { name: cfg.tmName || 'Training max' }); delete cfg.tmKey; }
        delete cfg.tmName;
      }
      if (!code && cfg && !cfg.inc) cfg = null; // double progression only carries a custom increment
      let ssi = null;
      if (pe.supersetId) { if (!ss.has(pe.supersetId)) ss.set(pe.supersetId, ss.size + 1); ssi = ss.get(pe.supersetId); }
      return trimTail([xi, pe.sets ?? 3, pe.repMin ?? 8, pe.repMax ?? pe.repMin ?? 8, pe.rest ?? 120, ssi, code, cfg, pe.note ? String(pe.note).slice(0, 140) : null]);
    });
    return trimTail([d.name || '', d.day || '', list]);
  });
  const c = program.cycle && Array.isArray(program.cycle.weeks) && program.cycle.weeks.length
    ? [program.cycle.repeat === false ? 0 : 1, program.cycle.weeks.map(w => trimTail([w.label || '', w.deload ? 1 : 0]))]
    : null;
  const out = { v: SHARE_VERSION, n: String(program.name || 'Shared program').slice(0, 80), x: table, d: days };
  if (program.split) out.s = String(program.split).slice(0, 60);
  if (program.weeks) out.w = program.weeks;
  if (c) out.c = c;
  if (program.weekRule === 'calendar') out.wr = 'c';
  return out;
}

// → { name, split, weeks, cycle, progressionNote, description, refs: [{ id, name, group, equipment, unit }],
//     days: [{ name, day, exercises: [{ ref, sets, repMin, repMax, rest, ss, scheme, schemeCfg, tmRef, note }] }] }
export function unpackProgram(p) {
  if (!p || typeof p !== 'object' || p.v !== SHARE_VERSION || !Array.isArray(p.d) || !Array.isArray(p.x)) throw new Error('This link is not a Lift Log program.');
  const num = (v, lo, hi, dflt) => (Number.isFinite(+v) ? Math.min(hi, Math.max(lo, +v)) : dflt);
  const str = (v, max = 80) => (typeof v === 'string' ? v.slice(0, max) : '');
  const refs = p.x.slice(0, 400).map(r => {
    const a = Array.isArray(r) ? r : [];
    const id = str(a[0], 80);
    const derived = /^fx_/.test(id) ? id.slice(3).replace(/_/g, ' ') : '';
    return { id, name: str(a[1], 80) || derived || 'Exercise', group: str(a[2], 30) || null, equipment: str(a[3], 30) || null, unit: a[4] === 's' ? 'sec' : 'reps' };
  });
  const days = p.d.slice(0, 14).map(dd => {
    const a = Array.isArray(dd) ? dd : [];
    return {
      name: str(a[0], 60) || 'Day', day: str(a[1], 3),
      exercises: (Array.isArray(a[2]) ? a[2] : []).slice(0, 40).map(e => {
        const b = Array.isArray(e) ? e : [];
        const ref = num(b[0], 0, refs.length - 1, -1);
        const scheme = CODE_SCHEMES[b[6]] || 'double';
        let cfg = b[7] && typeof b[7] === 'object' && !Array.isArray(b[7]) ? { ...b[7] } : null;
        let tmRef = null;
        if (cfg && cfg.tk != null) { tmRef = num(cfg.tk, 0, refs.length - 1, null); delete cfg.tk; }
        if (cfg) cfg = sanitizeCfg(cfg);
        const repMin = num(b[2], 1, 600, 8);
        return {
          ref, sets: num(b[1], 1, 20, 3), repMin, repMax: Math.max(repMin, num(b[3], 1, 600, repMin)), rest: num(b[4], 0, 900, 120),
          ss: b[5] ? num(b[5], 1, 40, null) : null, scheme, schemeCfg: cfg, tmRef, note: str(b[8], 140) || null,
        };
      }).filter(e => e.ref >= 0),
    };
  });
  let cycle = null;
  if (Array.isArray(p.c) && Array.isArray(p.c[1]) && p.c[1].length) {
    cycle = { repeat: p.c[0] !== 0, weeks: p.c[1].slice(0, 24).map((w, i) => ({ label: str(Array.isArray(w) ? w[0] : '', 40) || `Week ${i + 1}`, ...(Array.isArray(w) && w[1] ? { deload: true } : {}) })) };
  }
  return {
    name: str(p.n, 80) || 'Shared program', split: str(p.s, 60), weeks: num(p.w, 1, 52, null),
    cycle, progressionNote: str(p.pn, 400), description: str(p.ds, 200), weekRule: p.wr === 'c' ? 'calendar' : null,
    refs, days,
  };
}

// Only plain numbers / strings / short number arrays survive (no prototype tricks, no huge payloads).
function sanitizeCfg(cfg) {
  const out = {};
  for (const [k, v] of Object.entries(cfg).slice(0, 20)) {
    if (k === '__proto__' || k === 'constructor' || k === 'prototype') continue;
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
    else if (typeof v === 'boolean') out[k] = v;
    else if (typeof v === 'string' && v.length <= 30) out[k] = v;
    else if (Array.isArray(v) && v.length <= 24 && v.every(x => typeof x === 'number' && Number.isFinite(x))) out[k] = v.slice();
    else if (k === 'weeks' && Array.isArray(v) && v.length <= 24) out[k] = v.map(wk => (Array.isArray(wk) ? wk.slice(0, 20).map(sp => ({ pct: +sp?.pct || 0, reps: +sp?.reps || 1, ...(sp?.sets ? { sets: +sp.sets } : {}), ...(sp?.amrap ? { amrap: true } : {}), ...(sp?.target ? { target: +sp.target } : {}) })) : []));
  }
  return out;
}

// ——— Bytes ↔ base64url ———————————————————————————————————————————————————

function toB64url(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function fromB64url(str) {
  const b = String(str).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b + '==='.slice((b.length + 3) % 4));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function streamBytes(bytes, stream) {
  const res = new Response(new Blob([bytes]).stream().pipeThrough(stream));
  return new Uint8Array(await res.arrayBuffer());
}
const hasCS = () => typeof CompressionStream === 'function';
const hasDS = () => typeof DecompressionStream === 'function';

export async function deflateRaw(bytes) {
  if (!hasCS()) return null;
  try { return await streamBytes(bytes, new CompressionStream('deflate-raw')); } catch { return null; }
}
export async function inflateRaw(bytes) {
  if (hasDS()) {
    try { return await streamBytes(bytes, new DecompressionStream('deflate-raw')); } catch { /* fall through */ }
  }
  return inflateRawSync(bytes);
}

// ——— Tokens and links ————————————————————————————————————————————————————

export async function encodeProgramToken(program, exercises = [], { compress = true } = {}) {
  const json = new TextEncoder().encode(JSON.stringify(packProgram(program, exercises)));
  const z = compress ? await deflateRaw(json) : null;
  return z && z.length < json.length ? 'z' + toB64url(z) : 'j' + toB64url(json);
}

export async function decodeProgramToken(token) {
  const t = String(token || '').trim();
  if (t.length < 4 || t.length > 20000) throw new Error('This link is incomplete.');
  let bytes;
  try { bytes = fromB64url(t.slice(1)); } catch { throw new Error('This link is damaged.'); }
  if (t[0] === 'z') {
    try { bytes = await inflateRaw(bytes); } catch { throw new Error('This link is damaged.'); }
  } else if (t[0] !== 'j') throw new Error('This link is not a Lift Log program.');
  let packed;
  try { packed = JSON.parse(new TextDecoder().decode(bytes)); } catch { throw new Error('This link is damaged.'); }
  return unpackProgram(packed);
}

// The page links open: VITE_PUBLIC_URL when set (native builds, whose own origin
// is not reachable from another phone), else this page.
export function shareBase() {
  let pub = '';
  try { pub = import.meta.env?.VITE_PUBLIC_URL || ''; } catch { pub = ''; }
  if (pub) return pub.endsWith('/') || /\.html?$/.test(pub) ? pub : pub + '/';
  if (typeof window === 'undefined' || !window.location) return 'https://liftlog.app/';
  const { origin, pathname } = window.location;
  return origin + pathname;
}

export async function encodeProgramLink(program, { exercises = [], base = shareBase() } = {}) {
  const token = await encodeProgramToken(program, exercises);
  const url = `${base}#p=${token}`;
  return { url, token, bytes: new TextEncoder().encode(url).length };
}

// A pasted link, a '#p=…' fragment or a bare token → the token (or null).
export function tokenFromText(text) {
  const s = String(text || '').trim();
  if (!s) return null;
  const m = s.match(/[#&?]p=([zj][A-Za-z0-9_-]+)/);
  if (m) return m[1];
  if (/^[zj][A-Za-z0-9_-]{8,}$/.test(s)) return s;
  return null;
}
export const linkHashToken = () => (typeof window !== 'undefined' ? tokenFromText(window.location.hash) : null);

// ——— Idempotent import ———————————————————————————————————————————————————
// A stable key for a decoded shared program (the same program shared twice,
// compressed or not, gives the same key). Stored on the imported copy as
// program.shared = { key, at }, so opening the same link again finds it.

function stableJSON(v) {
  if (Array.isArray(v)) return `[${v.map(stableJSON).join(',')}]`;
  if (v && typeof v === 'object') return `{${Object.keys(v).sort().filter(k => v[k] !== undefined).map(k => `${JSON.stringify(k)}:${stableJSON(v[k])}`).join(',')}}`;
  return JSON.stringify(v === undefined ? null : v);
}
export function sharedProgramKey(shared) {
  const s = stableJSON(shared || null);
  let h1 = 0x811c9dc5, h2 = 0x01000193;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
    h2 = Math.imul(h2 ^ c, 2246822507) >>> 0;
  }
  return 's' + h1.toString(36) + h2.toString(36);
}
// The program already imported from this shared program (not deleted), or null.
export function findSharedCopy(programs, key) {
  if (!key) return null;
  return (programs || []).find(p => p && p.shared && p.shared.key === key) || null;
}

// ——— Import ——————————————————————————————————————————————————————————————

const normName = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
let _n = 0;
const nid = (p) => p + Date.now().toString(36).slice(-4) + (++_n).toString(36) + Math.random().toString(36).slice(2, 6);

// Map every shared exercise onto this device: same id in the library → as is;
// a catalog id → copied from the catalog; else the same name in the library
// or catalog; else a new custom exercise. `catalogToExercise` turns a catalog
// entry into a library exercise (passed in to keep this module React-free).
// → { program (fresh ids, no TMs), newExercises (pass to ensureExercises first),
//     report: { library, catalog, renamed, created, names: { created: [] } } }
// `resolveName(name, pool, catalog)` (optional) is a looser name match, used only for
// the app's built-in exercises (x_ ids) the receiver no longer has.
export function materializeShared(shared, { exercises = [], catalog = null, catalogToExercise = null, resolveName = null } = {}) {
  const pool = [...exercises];
  const newExercises = [];
  const report = { library: 0, catalog: 0, matched: 0, created: 0, createdNames: [] };
  const fromCatalog = (entry) => {
    const ex = catalogToExercise ? catalogToExercise(entry) : { id: entry.id, name: entry.name, group: entry.group, equipment: entry.equipment, mechanic: entry.mechanic, secondary: entry.secondary || [] };
    if (!pool.some(p => p.id === ex.id)) { pool.push(ex); newExercises.push(ex); }
    return ex;
  };
  const resolved = shared.refs.map(r => {
    const own = r.id && pool.find(e => e.id === r.id);
    if (own) { report.library++; return own; }
    const cat = r.id && catalog ? catalog.find(e => e.id === r.id) : null;
    if (cat) { report.catalog++; return fromCatalog(cat); }
    const n = normName(r.name);
    const byName = pool.find(e => normName(e.name) === n);
    if (byName) { report.matched++; return byName; }
    const catName = catalog ? catalog.find(e => normName(e.name) === n) : null;
    if (catName) { report.matched++; return fromCatalog(catName); }
    if (resolveName && /^x_/.test(r.id || '')) {
      const loose = resolveName(r.name, pool, catalog);
      if (loose && loose.id) { report.matched++; return pool.find(e => e.id === loose.id) || (catalog && catalog.find(e => e.id === loose.id) ? fromCatalog(catalog.find(e => e.id === loose.id)) : loose); }
    }
    const ex = { id: nid('xc_'), name: r.name, group: r.group || 'Other', equipment: r.equipment || 'Other', secondary: [], mechanic: 'compound', yt: '', custom: true, ...(r.unit === 'sec' ? { unit: 'sec' } : {}) };
    pool.push(ex); newExercises.push(ex);
    report.created++; report.createdNames.push(ex.name);
    return ex;
  });
  const days = shared.days.map(d => {
    const ssIds = {};
    return {
      id: nid('day_'), name: d.name, day: d.day,
      exercises: d.exercises.map(e => {
        const ex = resolved[e.ref];
        const pe = {
          id: nid('pe_'), exerciseId: ex.id, name: ex.name, group: ex.group || 'Other', unit: ex.unit || 'reps',
          sets: e.sets, repMin: e.repMin, repMax: e.repMax, targetWeight: 0, rest: e.rest, yt: ex.yt || '', alternates: [],
        };
        if (e.ss) pe.supersetId = ssIds[e.ss] || (ssIds[e.ss] = nid('ss_'));
        if (e.note) pe.note = e.note;
        if (e.scheme !== 'double') {
          pe.scheme = e.scheme;
          pe.schemeCfg = { ...(e.schemeCfg || {}) };
          pe.schemeState = {};
        } else if (e.schemeCfg?.inc) pe.schemeCfg = { inc: e.schemeCfg.inc };
        if (e.tmRef != null && resolved[e.tmRef]) { pe.schemeCfg = { ...(pe.schemeCfg || {}), tmKey: resolved[e.tmRef].id, tmName: resolved[e.tmRef].name }; }
        return pe;
      }),
    };
  });
  const program = {
    name: shared.name, author: 'Shared with you', weeks: shared.weeks || 8, currentWeek: 1,
    split: shared.split || '', description: shared.description || '', days,
    ...(shared.progressionNote ? { progressionNote: shared.progressionNote } : {}),
    ...(shared.cycle ? { cycle: shared.cycle } : {}),
    ...(shared.weekRule ? { weekRule: shared.weekRule } : {}),
    startedAt: Date.now(),
  };
  return { program, newExercises, report };
}

// ——— Raw inflate (RFC 1951) for browsers without DecompressionStream ————————

const LBASE = [3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31, 35, 43, 51, 59, 67, 83, 99, 115, 131, 163, 195, 227, 258];
const LEXT = [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0];
const DBASE = [1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537, 2049, 3073, 4097, 6145, 8193, 12289, 16385, 24577];
const DEXT = [0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13];
const CLORDER = [16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15];

function huff(lengths) {
  const count = new Array(16).fill(0);
  for (const l of lengths) count[l]++;
  count[0] = 0;
  const offs = new Array(16).fill(0);
  for (let i = 1; i < 16; i++) offs[i] = offs[i - 1] + count[i - 1];
  const symbols = new Array(lengths.length);
  lengths.forEach((l, s) => { if (l) symbols[offs[l]++] = s; });
  return { count, symbols };
}

export function inflateRawSync(input) {
  const src = input instanceof Uint8Array ? input : new Uint8Array(input);
  let pos = 0, bitBuf = 0, bitCnt = 0;
  let out = new Uint8Array(Math.max(1024, src.length * 4));
  let len = 0;
  const need = (n) => {
    while (bitCnt < n) {
      if (pos >= src.length) throw new Error('inflate: truncated');
      bitBuf |= src[pos++] << bitCnt; bitCnt += 8;
    }
  };
  const bits = (n) => { need(n); const v = bitBuf & ((1 << n) - 1); bitBuf >>>= n; bitCnt -= n; return v; };
  const push = (b) => {
    if (len >= out.length) { const bigger = new Uint8Array(out.length * 2); bigger.set(out); out = bigger; }
    if (len > 4_000_000) throw new Error('inflate: too large');
    out[len++] = b;
  };
  const decode = (h) => {
    let code = 0, first = 0, index = 0;
    for (let l = 1; l < 16; l++) {
      code |= bits(1);
      const c = h.count[l];
      if (code - c < first) return h.symbols[index + (code - first)];
      index += c; first += c; first <<= 1; code <<= 1;
    }
    throw new Error('inflate: bad code');
  };
  const codes = (lh, dh) => {
    for (;;) {
      let sym = decode(lh);
      if (sym < 256) push(sym);
      else if (sym === 256) return;
      else {
        sym -= 257;
        if (sym >= 29) throw new Error('inflate: bad length');
        const l = LBASE[sym] + bits(LEXT[sym]);
        const ds = decode(dh);
        if (ds >= 30) throw new Error('inflate: bad distance');
        const dist = DBASE[ds] + bits(DEXT[ds]);
        if (dist > len) throw new Error('inflate: distance too far');
        for (let i = 0; i < l; i++) push(out[len - dist]);
      }
    }
  };
  let fixedL = null, fixedD = null;
  let last = 0;
  while (!last) {
    last = bits(1);
    const type = bits(2);
    if (type === 0) {
      bitBuf = 0; bitCnt = 0;
      if (pos + 4 > src.length) throw new Error('inflate: truncated');
      const n = src[pos] | (src[pos + 1] << 8);
      pos += 4;
      if (pos + n > src.length) throw new Error('inflate: truncated');
      for (let i = 0; i < n; i++) push(src[pos++]);
    } else if (type === 1) {
      if (!fixedL) {
        const l = new Array(288);
        for (let i = 0; i < 144; i++) l[i] = 8;
        for (let i = 144; i < 256; i++) l[i] = 9;
        for (let i = 256; i < 280; i++) l[i] = 7;
        for (let i = 280; i < 288; i++) l[i] = 8;
        fixedL = huff(l); fixedD = huff(new Array(30).fill(5));
      }
      codes(fixedL, fixedD);
    } else if (type === 2) {
      const nlen = bits(5) + 257, ndist = bits(5) + 1, ncode = bits(4) + 4;
      const cl = new Array(19).fill(0);
      for (let i = 0; i < ncode; i++) cl[CLORDER[i]] = bits(3);
      const ch = huff(cl);
      const lengths = [];
      while (lengths.length < nlen + ndist) {
        const sym = decode(ch);
        if (sym < 16) lengths.push(sym);
        else {
          let rep = 0, val = 0;
          if (sym === 16) { if (!lengths.length) throw new Error('inflate: bad repeat'); val = lengths[lengths.length - 1]; rep = 3 + bits(2); }
          else if (sym === 17) rep = 3 + bits(3);
          else rep = 11 + bits(7);
          for (let i = 0; i < rep; i++) lengths.push(val);
        }
      }
      codes(huff(lengths.slice(0, nlen)), huff(lengths.slice(nlen, nlen + ndist)));
    } else throw new Error('inflate: bad block');
  }
  return out.subarray(0, len);
}
